import { useCallback, useRef, useState } from 'react';
import {
  Platform,
  type NativeSyntheticEvent,
  type TextInput,
  type TextInputContentSizeChangeEventData,
} from 'react-native';

/**
 * Grow a `multiline` TextInput with its content, from one line up to
 * `maxHeight`.
 *
 * Both platforms need help, and they need different help:
 *
 * - **Native** reports real content height through `onContentSizeChange`, so
 *   the measured value can drive a height style directly.
 * - **Web** renders a `<textarea>`, whose `scrollHeight` never falls below the
 *   height already applied to it. A measured height there could only ever
 *   grow: the box would stay tall after the text was deleted. The fix is the
 *   standard one — clear the height, read `scrollHeight`, apply it.
 *
 * A `multiline` input also needs `rows={1}`, or the browser's own default of
 * two rows sets the resting height (react-native-web only forwards `rows` when
 * you pass it: TextInput/index.js, `supportedProps.rows`). That default is why
 * a one-line pill renders two lines tall with its placeholder stuck to the top.
 */
export function useAutoGrowInput(maxHeight: number) {
  const ref = useRef<TextInput>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  // Web only: the height measure() just applied, reported for callers that
  // style around it (CommentComposer's multi-line shape). Never fed back as
  // a style, which would undo the clear-then-read above.
  const [measuredHeight, setMeasuredHeight] = useState<number | undefined>(undefined);

  /** Web only; no-op elsewhere. Safe to call on every keystroke. */
  const measure = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const node = ref.current as unknown as HTMLTextAreaElement | null;
    // Guard the shape: inside a sheet this ref belongs to gorhom's input.
    if (!node?.style) return;
    node.style.height = 'auto';
    const next = Math.min(node.scrollHeight, maxHeight);
    node.style.height = `${next}px`;
    setMeasuredHeight(next);
  }, [maxHeight]);

  const onContentSizeChange = useCallback(
    (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      if (Platform.OS === 'web') return;
      setHeight(Math.min(e.nativeEvent.contentSize.height, maxHeight));
    },
    [maxHeight]
  );

  return {
    ref,
    height,
    /** The input's current height on either platform, for styling around it. */
    contentHeight: Platform.OS === 'web' ? measuredHeight : height,
    measure,
    onContentSizeChange,
  };
}
